import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const BuyerDashboard = () => {
  const { currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await api.getBuyerDashboard();
      setData(result);
    } catch (error) {
      console.error('Failed to load buyer dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await api.addBuyerNote(newNote);
      setNewNote('');
      loadData();
    } catch (error) {
      alert('Failed to add note: ' + error.message);
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.deleteBuyerNote(noteId);
      loadData();
    } catch (error) {
      alert('Failed to delete note: ' + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getReconLevelLabel = (level) => {
    if (level <= 2) return 'Light';
    if (level === 3) return 'Medium';
    return 'Heavy';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
        <div className="text-lg text-neutral-500">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
        <div className="text-lg text-red-500">Failed to load data</div>
      </div>
    );
  }

  const { spending, inventory, recentPurchases, notes } = data;

  return (
    <div className="min-h-screen bg-[#f8f9fb]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0] shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#0f172a]">Buyer Dashboard</h1>
              <p className="text-sm text-[#64748b]">Your buying guide at a glance</p>
            </div>
            <button
              onClick={loadData}
              className="px-4 py-2 text-sm bg-[#f1f5f9] text-[#475569] rounded-lg hover:bg-[#e2e8f0] transition-colors"
            >
              ↻ Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Spending Power Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Comfortable Spend */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
            <h2 className="text-lg font-medium opacity-90 mb-2">Comfortable Spend</h2>
            <p className="text-4xl font-bold">{formatCurrency(spending.comfortableSpend)}</p>
          </div>

          {/* Max Buying Power */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
            <h2 className="text-lg font-medium opacity-90 mb-2">Max Buying Power</h2>
            <p className="text-4xl font-bold">{formatCurrency(spending.maxBuyingPower)}</p>
          </div>
        </div>

        {/* Cash vs Floorplan Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-[#e2e8f0] p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm font-medium text-[#374151]">Cash Available</span>
            </div>
            <p className="text-2xl font-bold text-[#0f172a] ml-5">{formatCurrency(spending.cashBuyingPower || 0)}</p>
            <p className="text-xs text-[#64748b] ml-5 mt-1">Bank balance minus obligations</p>
          </div>
          <div className="bg-purple-50 rounded-xl border border-purple-200 p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-sm font-medium text-purple-800">Floorplan Available</span>
            </div>
            <p className="text-2xl font-bold text-purple-900 ml-5">{formatCurrency(spending.floorplanAvailable)}</p>
            <p className="text-xs text-purple-600 ml-5 mt-1 font-medium">Reserve for high-value purchases only</p>
            <div className="mt-3 ml-5">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-purple-600">{formatCurrency(spending.floorplanUsed)} / {formatCurrency(spending.floorplanLimit)} used</span>
              </div>
              <div className="h-2 bg-purple-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${((spending.floorplanUsed / spending.floorplanLimit) > 0.8) ? 'bg-red-500' : 'bg-purple-500'}`}
                  style={{ width: `${Math.min((spending.floorplanUsed / spending.floorplanLimit) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* What We Need Section */}
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Current Inventory Mix</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-[#16a34a]">{inventory.reconLevelBreakdown.light}</p>
              <p className="text-sm text-[#15803d] font-medium">Light Recon</p>
              <p className="text-xs text-[#64748b]">Level 1-2</p>
            </div>
            <div className="bg-[#fefce8] border border-[#fef08a] rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-[#ca8a04]">{inventory.reconLevelBreakdown.medium}</p>
              <p className="text-sm text-[#a16207] font-medium">Medium Recon</p>
              <p className="text-xs text-[#64748b]">Level 3</p>
            </div>
            <div className="bg-[#fef2f2] border border-[#fecaca] rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-[#dc2626]">{inventory.reconLevelBreakdown.heavy}</p>
              <p className="text-sm text-[#b91c1c] font-medium">Heavy Recon</p>
              <p className="text-xs text-[#64748b]">Level 4-5</p>
            </div>
            <div className="bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-[#475569]">{inventory.totalCount}</p>
              <p className="text-sm text-[#64748b] font-medium">Total Inventory</p>
              <p className="text-xs text-[#64748b]">{inventory.carsInRecon} in recon</p>
            </div>
          </div>
        </div>

        {/* Salvage/Copart Section - always show */}
        {inventory.salvage && (
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6">
            <h2 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
              <span className="text-xl">🚗</span> Salvage/Copart Units
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/70 rounded-xl p-4 text-center border border-amber-200">
                <p className="text-3xl font-bold text-amber-700">{inventory.salvage.count}</p>
                <p className="text-sm text-amber-800 font-medium">Units</p>
                <p className="text-xs text-amber-600">Copart/IAAI</p>
              </div>
              <div className="bg-white/70 rounded-xl p-4 text-center border border-amber-200">
                <p className="text-3xl font-bold text-amber-700">{formatCurrency(inventory.salvage.totalInvested)}</p>
                <p className="text-sm text-amber-800 font-medium">Invested</p>
                <p className="text-xs text-amber-600">Purchase cost</p>
              </div>
              <div className="bg-white/70 rounded-xl p-4 text-center border border-amber-200">
                <p className="text-3xl font-bold text-green-600">{formatCurrency(inventory.salvage.totalExpected)}</p>
                <p className="text-sm text-amber-800 font-medium">Expected Return</p>
                <p className="text-xs text-green-600">
                  +{formatCurrency(inventory.salvage.totalExpected - inventory.salvage.totalInvested)} potential
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Purchases */}
          <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
            <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Recent Purchases (30 days)</h2>
            {recentPurchases.length > 0 ? (
              <div className="space-y-3">
                {recentPurchases.map(vehicle => (
                  <div key={vehicle.id} className="flex items-center justify-between p-3 bg-[#f8f9fb] rounded-lg">
                    <div>
                      <p className="font-medium text-[#0f172a]">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </p>
                      <p className="text-xs text-[#64748b]">
                        #{vehicle.stockNumber} • {formatDate(vehicle.purchaseDate)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#0f172a]">
                        {formatCurrency(vehicle.purchasePrice || 0)}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        vehicle.reconLevel <= 2 ? 'bg-green-100 text-green-700' :
                        vehicle.reconLevel === 3 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {getReconLevelLabel(vehicle.reconLevel)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#64748b] py-8">No recent purchases</p>
            )}
          </div>

          {/* Notes Section */}
          <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
            <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Notes & Communication</h2>
            
            {/* Add Note Form */}
            <div className="mb-4">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="w-full h-20 px-3 py-2 border border-[#e2e8f0] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingNote ? 'Adding...' : 'Add Note'}
                </button>
              </div>
            </div>

            {/* Notes List */}
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {notes.length > 0 ? (
                notes.map(note => (
                  <div key={note.id} className="p-3 bg-[#f8f9fb] rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-[#374151] whitespace-pre-wrap">{note.content}</p>
                      {(note.authorId === currentUser?.id || currentUser?.role === 'admin') && (
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-500 hover:text-red-700 text-xs flex-shrink-0"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-[#64748b] mt-2">
                      {note.authorName} • {formatDateTime(note.createdAt)}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-center text-[#64748b] py-4">No notes yet</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BuyerDashboard;
